// Cross-cut state for the app shell's top bar. Pages register their
// title (+ optional subtitle and parent breadcrumb) and the shell
// reads it. This is the canonical "current page identity" channel.
//
// Why a context, not Zustand: CLAUDE.md routes UI state that is
// genuinely *contextual* (current user, current tenant, current
// pharmacy, theme) to React Context. The current page is in that
// family — it's a property of the surrounding shell, not a piece
// of cross-cutting domain state.
//
// API:
//   - <PageHeaderProvider> wraps the shell.
//   - usePageHeader() reads the current header (or null).
//   - useSetPageHeader({ title, subtitle?, parentLabel?, parentTo? })
//     registers the page's metadata. Called from a page body during
//     render; sets on mount + when fields change, clears on unmount.
//
// Detail pages with async-loaded titles should call useSetPageHeader
// once the data lands (inside the same useEffect that owns the
// fetch, or after a useState read settles).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type PageHeader = {
  title: string;
  subtitle?: string;
  // Single-parent breadcrumb. Brief asks for "Routes / Thu, May 14"
  // style — that's parent + current, no deeper nesting today. If we
  // ever need true breadcrumb chains we can grow this to an array.
  parentLabel?: string;
  parentTo?: string;
  // Pages that render their own headline (e.g. /dispatch with the
  // day stepper + "Monday Delivery") can set this to suppress the
  // shell's top bar — otherwise the page label renders twice in a
  // row. The sidebar still labels the page; sign-out lives there.
  hideTopBar?: boolean;
};

type ContextValue = {
  header: PageHeader | null;
  setHeader: (next: PageHeader | null) => void;
};

const PageHeaderCtx = createContext<ContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }): JSX.Element {
  const [header, setHeaderState] = useState<PageHeader | null>(null);
  // Stable setter reference so consumers can put it in effect deps
  // without retriggering.
  const setHeader = useCallback((next: PageHeader | null) => {
    setHeaderState(next);
  }, []);
  const value = useMemo<ContextValue>(
    () => ({ header, setHeader }),
    [header, setHeader],
  );
  return <PageHeaderCtx.Provider value={value}>{children}</PageHeaderCtx.Provider>;
}

export function usePageHeader(): PageHeader | null {
  const ctx = useContext(PageHeaderCtx);
  return ctx?.header ?? null;
}

// Page-side hook. Pass plain string fields; the effect re-runs when
// any of them changes. On unmount, the header clears so a stale
// title doesn't bleed across navigations.
export function useSetPageHeader(input: PageHeader): void {
  const ctx = useContext(PageHeaderCtx);
  const setHeader = ctx?.setHeader;
  const { title, subtitle, parentLabel, parentTo, hideTopBar } = input;
  useEffect(() => {
    if (!setHeader) return;
    const next: PageHeader = { title };
    if (subtitle) next.subtitle = subtitle;
    if (parentLabel) next.parentLabel = parentLabel;
    if (parentTo) next.parentTo = parentTo;
    if (hideTopBar) next.hideTopBar = true;
    setHeader(next);
    return () => {
      setHeader(null);
    };
  }, [setHeader, title, subtitle, parentLabel, parentTo, hideTopBar]);
}
